import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { t } from '../utils/translations';
import LanguageSelector from './LanguageSelector';
import { TERMINAL_ENABLED } from '../config/features';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const location = useLocation();
  const { language } = useLanguage();
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  useEffect(() => {
    setMoreOpen(false);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onDoc = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const primary = [
    { to: '/strategies', key: 'nav.strategies' },
    { to: '/performance', key: 'nav.performance' },
    { to: '/reports', key: 'nav.reports' },
    ...(TERMINAL_ENABLED ? [{ to: '/terminal', key: 'nav.macroTerminal' }] : []),
    { to: '/research', key: 'nav.research' },
  ];

  const moreItems = [
    { to: '/portfolio', key: 'nav.portfolio' },
    { to: '/quant-lab', key: 'nav.quantLab' },
    { to: '/yggdrasil', key: 'nav.engine' },
    { to: '/about', key: 'nav.about' },
    { to: '/institutional', key: 'nav.institutional' },
  ];

  const moreActive = moreItems.some((item) => isActive(item.to));

  const linkStyle = { textDecoration: 'none' };

  const navLink = ({ to, key, onClick }) => (
    <Link
      key={to}
      to={to}
      className={`nav-link ${isActive(to) ? 'active' : ''}`}
      style={linkStyle}
      onClick={onClick}
    >
      {t(key, language)}
    </Link>
  );

  return (
    <>
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        padding: window.innerWidth < 768 ? '0 20px' : '0 48px',
        height: '72px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: scrolled ? 'rgba(6,6,14,0.92)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(255,255,255,0.05)' : 'none',
        transition: 'all 0.4s ease',
      }}>
        <Link to="/" style={{ textDecoration: 'none', flexShrink: 0 }}>
          <div style={{
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            border: '2px solid #00ffc8',
            boxShadow: '0 0 12px rgba(0,255,200,0.4)',
            overflow: 'hidden',
            cursor: 'pointer',
            background: '#0a0a12',
          }}>
            <img src="/logo-quant.png" alt="HMA Quant" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        </Link>

        <div className="desktop-nav" style={{
          display: 'flex',
          gap: '28px',
          alignItems: 'center',
          marginLeft: 'auto',
          paddingLeft: '40px',
        }}>
          {primary.map((item) => navLink(item))}

          <div ref={moreRef} style={{ position: 'relative' }}>
            <button
              type="button"
              className={`nav-link ${moreActive ? 'active' : ''}`}
              onClick={() => setMoreOpen((v) => !v)}
              aria-expanded={moreOpen}
              style={{ background: 'none' }}
            >
              {t('nav.more', language)}{' '}
              <span style={{ display: 'inline-block', transition: 'transform 160ms cubic-bezier(0.23, 1, 0.32, 1)', transform: moreOpen ? 'rotate(180deg)' : 'none' }}>▾</span>
            </button>
            <AnimatePresence>
            {moreOpen && (
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: -4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: -4, transition: { duration: 0.12 } }}
                transition={{ duration: 0.16, ease: [0.23, 1, 0.32, 1] }}
                style={{
                transformOrigin: 'top right',
                position: 'absolute',
                top: '120%',
                right: 0,
                minWidth: '220px',
                background: 'rgba(10,10,18,0.98)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '10px',
                padding: '8px',
                boxShadow: '0 16px 40px rgba(0,0,0,0.45)',
              }}>
                {moreItems.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMoreOpen(false)}
                    style={{
                      display: 'block',
                      padding: '10px 14px',
                      color: isActive(item.to) ? '#00ffc8' : '#bbb',
                      textDecoration: 'none',
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: '12px',
                      borderRadius: '6px',
                    }}
                  >
                    {t(item.key, language)}
                  </Link>
                ))}
              </motion.div>
            )}
            </AnimatePresence>
          </div>

          <div style={{ marginLeft: '8px', paddingLeft: '18px', borderLeft: '1px solid rgba(255,255,255,0.1)' }}>
            <LanguageSelector />
          </div>

          <Link
            to={user ? '/account' : '/login'}
            style={{ paddingLeft: '18px', borderLeft: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center' }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill={user ? '#00ffc8' : 'none'} stroke={user ? '#00ffc8' : '#666'} strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </Link>
        </div>

        <button
          className="mobile-hamburger"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          style={{ display: 'none', background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px', marginLeft: 'auto' }}
          aria-label={t('nav.toggleMenu', language)}
        >
          <motion.svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#00ffc8" strokeWidth="2" strokeLinecap="round" initial={false} animate={mobileMenuOpen ? 'open' : 'closed'}>
            <motion.line x1="3" y1="6" x2="21" y2="6" variants={{ closed: { rotate: 0, y: 0 }, open: { rotate: 45, y: 6 } }} transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }} style={{ originX: '12px', originY: '6px' }} />
            <motion.line x1="3" y1="12" x2="21" y2="12" variants={{ closed: { opacity: 1 }, open: { opacity: 0 } }} transition={{ duration: 0.12 }} />
            <motion.line x1="3" y1="18" x2="21" y2="18" variants={{ closed: { rotate: 0, y: 0 }, open: { rotate: -45, y: -6 } }} transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }} style={{ originX: '12px', originY: '18px' }} />
          </motion.svg>
        </button>
      </nav>

      <AnimatePresence>
      {mobileMenuOpen && (
        <motion.div
          className="mobile-menu-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.15 } }}
          transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(6, 6, 14, 0.98)', zIndex: 200,
            display: 'flex', flexDirection: 'column', padding: '80px 24px 24px', overflowY: 'auto',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) closeMobileMenu(); }}
        >
          <button onClick={closeMobileMenu} style={{ position: 'absolute', top: '20px', right: '20px', background: 'transparent', border: 'none' }} aria-label={t('nav.closeMenu', language)}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#00ffc8" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '24px' }}>
            {primary.map((item) => navLink({ ...item, onClick: closeMobileMenu }))}
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: '#555', padding: '16px 16px 4px', letterSpacing: '2px' }}>
              {t('nav.more', language).toUpperCase()}
            </div>
            {moreItems.map((item) => navLink({ ...item, onClick: closeMobileMenu }))}
            <Link
              to={user ? '/account' : '/login'}
              className={`nav-link ${isActive(user ? '/account' : '/login') ? 'active' : ''}`}
              style={linkStyle}
              onClick={closeMobileMenu}
            >
              {user ? t('nav.account', language) : t('nav.login', language)}
            </Link>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <LanguageSelector />
          </div>
        </motion.div>
      )}
      </AnimatePresence>

      <style>{`
        @media (max-width: 1099px) {
          .desktop-nav { display: none !important; }
          .mobile-hamburger { display: block !important; }
          .nav-link {
            min-height: 52px;
            display: flex;
            align-items: center;
            padding: 12px 16px;
            border-radius: 8px;
            font-size: 18px;
          }
          .nav-link:hover, .nav-link.active { background: rgba(0, 255, 200, 0.1); }
        }
        @media (min-width: 1100px) {
          .mobile-hamburger { display: none !important; }
          .mobile-menu-overlay { display: none !important; }
        }
      `}</style>
    </>
  );
}
